import { APP_CONFIG } from '../constants/config';
/**
 * @typedef {import('../interfaces/IRegistroInteresado').IRegistroInteresado} IRegistroInteresado
 * @typedef {import('../interfaces/IRegistroInteresado').IRegistroInteresadoSP} IRegistroInteresadoSP
 */

const context = typeof window !== 'undefined' ? window['_spPageContextInfo'] : null;
const currentSharePointConfig =
  APP_CONFIG.sharePoint.environments[APP_CONFIG.environment as 'PRU' | 'DEV' | 'PRD'];

const currentSalesforceConfig =
  APP_CONFIG.salesforce.environments[APP_CONFIG.environment as 'PRU' | 'DEV' | 'PRD'];

const webUrl = currentSharePointConfig.webUrl;
const SALESFORCE_ORG_ID = currentSalesforceConfig.orgId;
const SALESFORCE_WEB_TO_LEAD_URL = currentSalesforceConfig.webToLeadUrl;

/**
 * Mapeo de tipos de documento a códigos Salesforce.
 * NOTA: Los values del array tiposDocumento en App.jsx ya contienen los IDs de Salesforce,
 * por lo que no se requiere mapeo adicional. Este objeto se mantiene como referencia documentada.
 * 
 * Mapeo de referencia:
 * 1 = CC (Cédula Ciudadanía)
 * 2 = NIT (NIT)
 * 3 = TI (Tarjeta Identidad)
 * 4 = CE (Cédula Extranjería)
 * 5 = PA (Pasaporte)
 * 6 = FN (Fecha de Nacimiento)
 * 7 = RC (Registro Civil)
 * 8 = NU (NUIP)
 * 9 = MS (Menor sin Identificación)
 * 11 = SC (Salvoconducto)
 * 12 = AS (Adulto sin Identificar)
 * 13 = MMS (Menor sin Identificar)
 * 14 = PT (Permiso por Protección Temporal)
 * 15 = CN (Certificado de Nacido Vivo)
 */
const TipoDocumentoReference = {
  CC: '1',
  NIT: '2',
  TI: '3',
  CE: '4',
  PA: '5',
  FN: '6',
  RC: '7',
  NU: '8',
  MS: '9',
  SC: '11',
  AS: '12',
  MMS: '13',
  PT: '14',
  CN: '15'
};

/**
* @param {string} listTitle
* @param {IRegistroInteresado} data
*/
export const addRegistro = async (listTitle, data) => {
  // Preparar el objeto con metadata de SharePoint
  const spData = {
    __metadata: {
      type: 'SP.Data.FormularioContactoListItem'
    },
    ...data
  };
  
  const response = await fetch(`${webUrl}/_api/web/lists/getByTitle('${listTitle}')/items`, {
    method: 'POST',
    headers: {
      Accept: 'application/json;odata=verbose',
      'Content-Type': 'application/json;odata=verbose',
      'X-RequestDigest': await getFormDigestValue(),
    },
    body: JSON.stringify(spData),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Error al crear registro: ${response.status} - ${errorText}`);
  }
  
  return await response.json();
};

export const addRegistroProd = (listTitle, data) => {

  if (!currentSharePointConfig.enableLeadSend) {
    return Promise.resolve({
      skipped: true,
      message: `Envío a SharePoint deshabilitado para el ambiente ${APP_CONFIG.environment}`,
    });
  }
  const spData = {
    __metadata: {
      type: 'SP.Data.FormulariocontactoListItem'
    },
    ...data
  };

  return fetch(`${webUrl}/_api/contextinfo`, {
    method: 'POST',
    headers: {
      Accept: 'application/json;odata=verbose',
    },
  })
    .then(r => r.json())
    .then(d => {
      const digest = d.d.GetContextWebInformation.FormDigestValue;
      return fetch(`${webUrl}/_api/web/lists/getbytitle('${listTitle}')/items`, {
        method: 'POST',
        headers: {
          Accept: 'application/json;odata=verbose',
          'Content-Type': 'application/json;odata=verbose',
          'X-RequestDigest': digest,
        },
        body: JSON.stringify(spData),
      });
    })
    .then(response => {
      if (!response.ok) {
        return response.text().then(errorText => {
          throw new Error(`Error al crear registro: ${response.status} - ${errorText}`);
        });
      }
      return response.json();
    });
};

/**
 * @returns {Promise<string>}
 */
async function getFormDigestValue() {
  const response = await fetch(`${webUrl}/_api/contextinfo`, {
    method: 'POST',
    headers: {
      Accept: 'application/json;odata=verbose',
      'Content-Type': 'application/json;odata=verbose',
    },
  });
  const data = await response.json();
  return data.d.GetContextWebInformation.FormDigestValue;
}

/**
 * Envia los datos del formulario a Salesforce mediante Web-to-Lead.
 * @param {IRegistroInteresado} formData
 * @returns {Promise<boolean>}
 */
export const sendToSalesforce = async function(formData) {
  try {    

    const nombreParts = formData.nombreCompleto.trim().split(' ');
    const firstName = nombreParts.slice(0, 2).join(' ') || '';
    const lastName = nombreParts.slice(2).join(' ') || nombreParts[0];

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = SALESFORCE_WEB_TO_LEAD_URL;
    form.target = 'salesforce-iframe';
    form.style.display = 'none';

    const fields = {
      oid: SALESFORCE_ORG_ID,
      retURL: typeof window !== 'undefined' ? window.location.href : '',
      first_name: firstName,
      last_name: lastName,
      [currentSalesforceConfig.fields.tipoDocumento]: formData.tipoDocumento || '1',
      [currentSalesforceConfig.fields.numeroDocumento]: formData.numeroDocumento,
      mobile: formData.telefono,
      email: formData.email,
      city: formData.ciudad,
      recordType: APP_CONFIG.salesforce.recordType,
      lead_source: APP_CONFIG.salesforce.leadSource
    };

    if (formData.mediosContacto.email) {
      fields[currentSalesforceConfig.fields.medioEmail] = '1';
    }
    if (formData.mediosContacto.celular) {
      fields[currentSalesforceConfig.fields.medioCelular] = '1';
    }
    if (formData.mediosContacto.whatsapp) {
      fields[currentSalesforceConfig.fields.medioWhatsapp] = '1';
    }
    if (formData.autorizacion) {
      fields[currentSalesforceConfig.fields.autorizacion] = '1';
    }

    fields[currentSalesforceConfig.fields.origen] = 'PC';
    fields[currentSalesforceConfig.fields.subOrigen] = 'PC';
    fields[currentSalesforceConfig.fields.tipoCliente] = 'Individual';

    console.log('[Salesforce] Enviando lead (sendToSalesforce)', {
      ambiente: APP_CONFIG.environment,
      url: SALESFORCE_WEB_TO_LEAD_URL,
      nombre: `${firstName} ${lastName}`,
      email: formData.email,
      telefono: formData.telefono,
      ciudad: formData.ciudad,
      tipoDocumento: formData.tipoDocumento,
      numeroDocumento: formData.numeroDocumento,
      mediosContacto: formData.mediosContacto,
      autorizacion: formData.autorizacion,
      fields,
    });

    for (const [name, value] of Object.entries(fields)) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value || '';
      form.appendChild(input);
    }   

    if (typeof document !== 'undefined') {
      document.body.appendChild(form);
      form.submit();

      setTimeout(() => {
        document.body.removeChild(form);
      }, 2000);
    }

    return true;
  } catch (error) {
    console.error('⚠ Error al enviar a Salesforce:', error);
    return false;
  }
};

/**
 * Envia los datos del formulario a Salesforce para el Plan Básico de Salud (beneficios).
 * Versión específica del Web-to-Lead para beneficiarios.
 * @param {IRegistroInteresado} formData
 * @returns {Promise<boolean>}
 */
export const sendToSalesforceBasicPlan = async function(formData) {
  try {  

    const nombreParts = formData.nombreCompleto.trim().split(' ');
    const firstName = nombreParts.slice(0, 2).join(' ') || '';
    const lastName = nombreParts.slice(2).join(' ') || nombreParts[0];

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = SALESFORCE_WEB_TO_LEAD_URL;
    form.target = 'salesforce-iframe';
    form.style.display = 'none';

    // URL de retorno específica para el plan de beneficios
    const retURL = currentSalesforceConfig.retUrls.beneficios;
    const fields = {
      oid: SALESFORCE_ORG_ID,
      retURL: retURL,
      first_name: firstName,
      last_name: lastName,
      [currentSalesforceConfig.fields.tipoDocumento]: formData.tipoDocumento || '1',
      [currentSalesforceConfig.fields.numeroDocumento]: formData.numeroDocumento,
      mobile: formData.telefono,
      email: formData.email,
      city: formData.ciudad,
      recordType: APP_CONFIG.salesforce.recordType,
      lead_source: APP_CONFIG.salesforce.leadSource
    };

    if (formData.mediosContacto.email) {
      fields[currentSalesforceConfig.fields.medioEmail] = '1';
    }
    if (formData.mediosContacto.celular) {
      fields[currentSalesforceConfig.fields.medioCelular] = '1';
    }
    if (formData.mediosContacto.whatsapp) {
      fields[currentSalesforceConfig.fields.medioWhatsapp] = '1';
    }
    if (formData.autorizacion) {
      fields[currentSalesforceConfig.fields.autorizacion] = '1';
    }

    fields[currentSalesforceConfig.fields.origen] = 'EP';
    fields[currentSalesforceConfig.fields.subOrigen] = 'EP';
    fields[currentSalesforceConfig.fields.tipoCliente] = 'Individual';

    console.log('[Salesforce] Enviando lead (Plan Básico - PBS)', {
      ambiente: APP_CONFIG.environment,
      url: SALESFORCE_WEB_TO_LEAD_URL,
      nombre: `${firstName} ${lastName}`,
      email: formData.email,
      telefono: formData.telefono,
      ciudad: formData.ciudad,
      tipoDocumento: formData.tipoDocumento,
      numeroDocumento: formData.numeroDocumento,
      mediosContacto: formData.mediosContacto,
      autorizacion: formData.autorizacion,
      fields,
    });

    for (const [name, value] of Object.entries(fields)) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value || '';
      form.appendChild(input);
    }   

    if (typeof document !== 'undefined') {
      document.body.appendChild(form);
      form.submit();

      setTimeout(() => {
        document.body.removeChild(form);
      }, 2000);
    }

    return true;
  } catch (error) {
    console.error('⚠ Error al enviar a Salesforce para Plan Básico:', error);
    return false;
  }
};

/**
 * Envia los datos del formulario a Salesforce para el Plan Complementario.
 * Versión específica del Web-to-Lead para plan complementario.
 * @param {IRegistroInteresado} formData
 * @returns {Promise<boolean>}
 */
export const sendToSalesforceComplementario = async function(formData) {
  try {   

    const nombreParts = formData.nombreCompleto.trim().split(' ');
    const firstName = nombreParts.slice(0, 2).join(' ') || '';
    const lastName = nombreParts.slice(2).join(' ') || nombreParts[0];

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = SALESFORCE_WEB_TO_LEAD_URL;
    form.target = 'salesforce-iframe';
    form.style.display = 'none';

    // URL de retorno específica para el plan complementario
    const retURL = currentSalesforceConfig.retUrls.complementario;
    const fields = {
      oid: SALESFORCE_ORG_ID,
      retURL: retURL,
      first_name: firstName,
      last_name: lastName,
      [currentSalesforceConfig.fields.tipoDocumento]: formData.tipoDocumento || '1',
      [currentSalesforceConfig.fields.numeroDocumento]: formData.numeroDocumento,
      mobile: formData.telefono,
      email: formData.email,
      city: formData.ciudad,
      recordType: APP_CONFIG.salesforce.recordType,
      lead_source: APP_CONFIG.salesforce.leadSource
    };

    if (formData.mediosContacto.email) {
      fields[currentSalesforceConfig.fields.medioEmail] = '1';
    }
    if (formData.mediosContacto.celular) {
      fields[currentSalesforceConfig.fields.medioCelular] = '1';
    }
    if (formData.mediosContacto.whatsapp) {
      fields[currentSalesforceConfig.fields.medioWhatsapp] = '1';
    }
    if (formData.autorizacion) {
      fields[currentSalesforceConfig.fields.autorizacion] = '1';
    }

    fields[currentSalesforceConfig.fields.origen] = 'CE';
    fields[currentSalesforceConfig.fields.subOrigen] = 'CE';
    fields[currentSalesforceConfig.fields.categoriaPlan] = 'PCE';
    fields[currentSalesforceConfig.fields.tipoPvos] = 'PCE';
    fields[currentSalesforceConfig.fields.tipoCliente] = 'Individual';

    console.log('[Salesforce] Enviando lead (Plan Complementario - PCE)', {
      ambiente: APP_CONFIG.environment,
      url: SALESFORCE_WEB_TO_LEAD_URL,
      nombre: `${firstName} ${lastName}`,
      email: formData.email,
      telefono: formData.telefono,
      ciudad: formData.ciudad,
      tipoDocumento: formData.tipoDocumento,
      numeroDocumento: formData.numeroDocumento,
      mediosContacto: formData.mediosContacto,
      autorizacion: formData.autorizacion,
      fields,
    });

    for (const [name, value] of Object.entries(fields)) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value || '';
      form.appendChild(input);
    }   

    if (typeof document !== 'undefined') {
      document.body.appendChild(form);
      form.submit();

      setTimeout(() => {
        document.body.removeChild(form);
      }, 2000);
    }

    return true;
  } catch (error) {
    console.error('⚠ Error al enviar a Salesforce para Plan Complementario:', error);
    return false;
  }
};

/**
 * Envia los datos del formulario a Salesforce para el Plan Vital.
 * Versión específica del Web-to-Lead para plan vital.
 * @param {IRegistroInteresado} formData
 * @returns {Promise<boolean>}
 */
export const sendToSalesforceVital = async function(formData) {
  try {
    const nombreParts = formData.nombreCompleto.trim().split(' ');
    const firstName = nombreParts.slice(0, 2).join(' ') || '';
    const lastName = nombreParts.slice(2).join(' ') || nombreParts[0];

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = SALESFORCE_WEB_TO_LEAD_URL;
    form.target = 'salesforce-iframe';
    form.style.display = 'none';

    // URL de retorno específica para el plan vital
    const retURL = currentSalesforceConfig.retUrls.vital;
    const fields = {
      oid: SALESFORCE_ORG_ID,
      retURL: retURL,
      first_name: firstName,
      last_name: lastName,
      [currentSalesforceConfig.fields.tipoDocumento]: formData.tipoDocumento || '1',
      [currentSalesforceConfig.fields.numeroDocumento]: formData.numeroDocumento,
      mobile: formData.telefono,
      email: formData.email,
      city: formData.ciudad,
      recordType: APP_CONFIG.salesforce.recordType,
      lead_source: APP_CONFIG.salesforce.leadSource
    };

    if (formData.mediosContacto.email) {
      fields[currentSalesforceConfig.fields.medioEmail] = '1';
    }
    if (formData.mediosContacto.celular) {
      fields[currentSalesforceConfig.fields.medioCelular] = '1';
    }
    if (formData.mediosContacto.whatsapp) {
      fields[currentSalesforceConfig.fields.medioWhatsapp] = '1';
    }
    if (formData.autorizacion) {
      fields[currentSalesforceConfig.fields.autorizacion] = '1';
    }

    fields[currentSalesforceConfig.fields.origen] = 'CE';
    fields[currentSalesforceConfig.fields.subOrigen] = 'CE';
    fields[currentSalesforceConfig.fields.categoriaPlan] = 'PCV';
    fields[currentSalesforceConfig.fields.tipoPvos] = 'PCV';
    fields[currentSalesforceConfig.fields.tipoCliente] = 'Individual';

    console.log('[Salesforce] Enviando lead (Plan Vital - PCV)', {
      ambiente: APP_CONFIG.environment,
      url: SALESFORCE_WEB_TO_LEAD_URL,
      nombre: `${firstName} ${lastName}`,
      email: formData.email,
      telefono: formData.telefono,
      ciudad: formData.ciudad,
      tipoDocumento: formData.tipoDocumento,
      numeroDocumento: formData.numeroDocumento,
      mediosContacto: formData.mediosContacto,
      autorizacion: formData.autorizacion,
      fields,
    });

    for (const [name, value] of Object.entries(fields)) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value || '';
      form.appendChild(input);
    }   

    if (typeof document !== 'undefined') {
      document.body.appendChild(form);
      form.submit();

      setTimeout(() => {
        document.body.removeChild(form);
      }, 2000);
    }

    return true;
  } catch (error) {
    console.error('⚠ Error al enviar a Salesforce para Plan Vital:', error);
    return false;
  }
};
